import { useState } from "react";
import { motion } from "framer-motion";
import {
  Check, MapPin, Camera, FileText, CalendarCheck, ShieldCheck,
  DollarSign, Users, Settings, Snowflake, ArrowRight, Zap, Menu, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import QuoteForm from "@/components/QuoteForm";
import { Link } from "react-router-dom";
import { locations } from "@/data/locations";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" },
  }),
};

const scrollToQuote = () => {
  document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
};

const HERO_IMG = "https://images.unsplash.com/photo-1781797221329-c3d083ab6d99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzgyMDc1MjMxfDA&ixlib=rb-4.1.0&q=85";
const HOW_IMG = "https://images.unsplash.com/photo-1733120725371-6c9e21bcc56e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB1c2luZyUyMHNtYXJ0cGhvbmUlMjBsYXB0b3AlMjBhdCUyMGhvbWUlMjBjb3p5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODIwNzg1NzB8MA&ixlib=rb-4.1.0&q=85";
const WINTER_IMG = "https://images.pexels.com/photos/7031414/pexels-photo-7031414.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const trustBadges = [
  "Transparent Pricing", "No Sales Visits", "Trusted Local Installers", "Financing Available",
];

const steps = [
  { icon: MapPin, title: "Enter Your Address", desc: "Tell us where you live so we can tailor recommendations and incentives to your area." },
  { icon: Camera, title: "Upload Photos", desc: "Share photos of your new install location or existing equipment — no appointment needed." },
  { icon: FileText, title: "Get Recommended Equipment & Pricing", desc: "Receive transparent pricing and equipment matched to your home." },
  { icon: CalendarCheck, title: "Choose Financing & Schedule", desc: "Pick a payment option that fits your budget and book your installation." },
];

const benefits = [
  { icon: DollarSign, title: "Transparent Pricing", desc: "Know what you'll pay before committing to an in-home appointment." },
  { icon: ShieldCheck, title: "No High-Pressure Sales", desc: "Avoid multiple contractor visits and confusing quotes." },
  { icon: Settings, title: "Recommended Equipment", desc: "Receive equipment recommendations tailored to your home." },
  { icon: Users, title: "Trusted Installation Network", desc: "Work with qualified local installers." },
  { icon: Zap, title: "Financing", desc: "Choose payment options that fit your budget." },
];

const faqs = [
  { q: "How accurate are online quotes?", a: "Our goal is to provide highly accurate estimates using your address, home details, photos, and information about your existing heating and cooling system. In some cases, additional information or a verification visit may be needed before finalizing your installation price." },
  { q: "Will I need a home visit?", a: "Many heat pump installs/replacements can be quoted remotely using photos and property information. For more complex projects, we may recommend a site visit to confirm equipment sizing, electrical requirements, or installation details before work begins." },
  { q: "What brands will be available?", a: "We're building relationships with leading heat pump manufacturers known for performance and reliability in Northeast climates. MrCool is one of them. Available brands may vary by location, but our goal is to recommend the best equipment for your home—not just a single brand." },
  { q: "Can I finance my installation?", a: "Yes. We plan to offer financing options that allow homeowners to spread payments over time, making it easier to upgrade to an energy-efficient heating and cooling system." },
  { q: "Will rebates and incentives be included?", a: "Yes. Our goal is to help homeowners identify available federal, state, utility, and local incentives and incorporate eligible rebates into the purchasing process whenever possible." },
];

const Logo = () => (
  <div className="flex items-center gap-2" data-testid="brand-logo">
    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white">
      <Snowflake className="h-5 w-5" strokeWidth={2} />
    </div>
    <span className="font-heading text-xl font-bold tracking-tight text-slate-950">
      Heat<span className="text-blue-600">Pump</span>HQ
    </span>
  </div>
);

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-950">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <Logo />
          <div className="hidden items-center gap-8 md:flex">
            <a href="#how" className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600" data-testid="nav-how">How It Works</a>
            <a href="#why" className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600" data-testid="nav-why">Why Us</a>
            <a href="#faq" className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600" data-testid="nav-faq">FAQ</a>
            <Button onClick={scrollToQuote} className="rounded-full bg-blue-600 px-6 font-medium text-white hover:bg-blue-700" data-testid="nav-cta-button">
              Get My Quote
            </Button>
          </div>
          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>
        {menuOpen && (
          <div className="border-t border-slate-200 bg-white px-6 py-4 md:hidden" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <a href="#how" onClick={() => setMenuOpen(false)} className="text-sm font-medium text-slate-700">How It Works</a>
              <a href="#why" onClick={() => setMenuOpen(false)} className="text-sm font-medium text-slate-700">Why Us</a>
              <a href="#faq" onClick={() => setMenuOpen(false)} className="text-sm font-medium text-slate-700">FAQ</a>
              <Button onClick={() => { setMenuOpen(false); scrollToQuote(); }} className="rounded-full bg-blue-600 text-white hover:bg-blue-700">Get My Quote</Button>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-sky-50 to-emerald-50">
        <div className="pointer-events-none absolute -right-24 -top-10 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:grid-cols-2 md:px-12 md:py-24">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
            For Massachusetts &amp; New Hampshire Homeowners
          </p>
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
            Heat Pump Installation Made Simple
          </h1>
          <h2 className="mt-4 font-heading text-xl font-medium leading-snug text-slate-700 sm:text-2xl">
            Get Your Heat Pump / Ductless Mini Split Price Online Before Anyone Visits Your Home
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 lg:text-lg">
            Skip the sales pitches and surprise quotes. Get transparent pricing, equipment
            recommendations, financing options, and installation scheduling — all online.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <Button onClick={scrollToQuote} className="h-auto rounded-full bg-blue-600 px-8 py-4 text-base font-medium text-white shadow-sm hover:bg-blue-700" data-testid="hero-primary-cta">
              Get My Quote <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button onClick={scrollToQuote} variant="outline" className="h-auto rounded-full border-blue-300 bg-white px-8 py-4 text-base font-medium text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-100 hover:text-blue-800" data-testid="hero-secondary-cta">
              Join the Early Access List
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {trustBadges.map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm font-medium text-slate-700" data-testid="trust-badge">
                <Check className="h-4 w-4 shrink-0 text-emerald-500" strokeWidth={3} />
                {b}
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
          <img src={HERO_IMG} alt="Modern home with heat pump" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl" data-testid="hero-image" />
          <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:block">
            <p className="text-sm font-medium text-slate-500">Average online quote</p>
            <p className="font-heading text-2xl font-bold text-slate-950">Under 5 minutes</p>
          </div>
        </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">Simple Online Process</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
              How It Works
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 lg:text-lg">
              Four simple steps to a warmer, cooler home — from online quote to heat pump or
              ductless mini split installation.
            </p>
          </div>
          <div className="mt-16 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="grid gap-6">
              {steps.map((s, i) => (
                <motion.div key={s.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                  className="flex gap-5 rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-transform hover:-translate-y-1" data-testid={`how-step-${i + 1}`}>
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
                    <s.icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Step {i + 1}</span>
                    </div>
                    <h3 className="mt-1 font-heading text-lg font-semibold text-slate-950">{s.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <img src={HOW_IMG} alt="Homeowner getting a quote online" className="aspect-[4/5] w-full rounded-3xl object-cover shadow-xl" data-testid="how-image" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY HOMEOWNERS */}
      <section id="why" className="bg-gradient-to-b from-white via-sky-50/60 to-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">Why Homeowners Choose Us</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
              A better way to buy heating &amp; cooling
            </h2>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => (
              <motion.div key={b.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-transform hover:-translate-y-1 hover:shadow-md" data-testid={`benefit-card-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <b.icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold text-slate-950">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILT FOR NORTHEAST */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:grid-cols-2 md:px-12">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <img src={WINTER_IMG} alt="Northeast home in winter" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl" data-testid="northeast-image" />
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-1.5 text-sm font-semibold text-blue-700">
              <Snowflake className="h-4 w-4" /> Cold-Climate Ready
            </div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Built for Northeast Homeowners
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 lg:text-lg">
              Designed specifically for Massachusetts and New Hampshire homeowners — from the
              Northwest Boston suburbs to New Hampshire's Mount Washington Valley — where cold
              climate heat pumps can significantly reduce heating costs and improve comfort, while
              also providing cooling in summer.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600 lg:text-lg">
              The platform simplifies access to modern, energy-efficient heat pump and ductless
              mini split installation while helping homeowners understand available heat pump
              rebates, incentives, and financing options.
            </p>
          </motion.div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-sky-50 to-blue-50 py-20 md:py-28">
        <div className="pointer-events-none absolute right-0 top-1/4 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-6 md:px-12">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Get Early Access &amp; Receive a Personalized Estimate
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Tell us a little about your home and current system. We're building a simpler way to
              shop for heat pumps online with transparent pricing, financing options, and trusted
              local installers.
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">FAQ</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Frequently Asked Questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-heading text-lg font-medium text-slate-950 hover:no-underline" data-testid={`faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center md:px-12">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Ready to Skip the HVAC Sales Process?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-300 lg:text-lg">
            Join the waitlist and help shape the future of heat pump installation.
          </p>
          <Button onClick={scrollToQuote} className="mt-8 h-auto rounded-full bg-emerald-500 px-10 py-4 text-base font-medium text-white shadow-sm hover:bg-emerald-600" data-testid="final-cta-button">
            Get Early Access <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white py-12">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <Logo />
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} HeatPumpHQ. Serving Northeast homeowners.</p>
          </div>
          <div className="mt-8 border-t border-slate-100 pt-6">
            <p className="text-sm font-semibold text-slate-700">Heat Pump Installation Service Areas</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {locations.map((l) => (
                <Link key={l.slug} to={`/heat-pump-installation-${l.slug}`} className="text-sm text-slate-500 transition-colors hover:text-blue-600" data-testid={`footer-area-${l.slug}`}>
                  {l.town}, {l.state}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
